/**
 * detectInitialCurrency
 * --------------------
 * Returns the seed value for `isCAD` in ShoppingCartContext when the visitor
 * has no prior localStorage choice. Only flips to CAD when BOTH signals point
 * to Canada — language preference AND IANA timezone — because ~97% of our
 * customers are US-based and a wrong default is worse than no smart default.
 *
 * Single-signal matches (en-CA on a US timezone = Canadian visiting US;
 * en-US on a Canadian timezone = US user with VPN; fr-FR on America/Toronto =
 * French national in Toronto) all default to USD and let the user toggle
 * once if needed.
 *
 * Once the user makes any explicit choice, useLocalStorage takes over and
 * this function is never consulted again for that browser.
 *
 * Also exports `shouldOfferCAD()` for the soft-prompt banner — see comment
 * on that function below.
 */

// IANA timezone identifiers for Canada, including legacy aliases. Compiled
// once at module load.
const CANADIAN_TIMEZONES = new Set([
    // Atlantic
    'America/Halifax',
    'America/Glace_Bay',
    'America/Moncton',
    'America/Goose_Bay',
    'America/Blanc-Sablon', // Quebec, north shore of Gulf of St. Lawrence
    // (Atlantic/Bermuda is Atlantic time but NOT Canada — deliberately excluded)
    // Newfoundland
    'America/St_Johns',
    // Eastern
    'America/Toronto',
    'America/Montreal', // legacy alias
    'America/Nipigon',
    'America/Thunder_Bay',
    'America/Iqaluit',
    'America/Pangnirtung',
    'America/Atikokan',
    // Central
    'America/Winnipeg',
    'America/Rainy_River',
    'America/Resolute',
    'America/Rankin_Inlet',
    // Saskatchewan (no DST)
    'America/Regina',
    'America/Swift_Current',
    // Mountain
    'America/Edmonton',
    'America/Cambridge_Bay',
    'America/Yellowknife',
    'America/Inuvik',
    // Pacific
    'America/Vancouver',
    'America/Dawson_Creek',
    'America/Creston', // BC, observes MST year-round
    'America/Fort_Nelson',
    'America/Whitehorse',
    'America/Dawson',
    // Canada/* legacy aliases
    'Canada/Atlantic',
    'Canada/Central',
    'Canada/Eastern',
    'Canada/Mountain',
    'Canada/Newfoundland',
    'Canada/Pacific',
    'Canada/Saskatchewan',
    'Canada/Yukon',
])

function hasCanadianLocale() {
    if (typeof navigator === 'undefined') return false
    // Prefer navigator.languages (ordered list of preferences). Fall back to
    // navigator.language (single string). Match any en-CA, fr-CA, etc.
    const langs =
        Array.isArray(navigator.languages) && navigator.languages.length > 0
            ? navigator.languages
            : [navigator.language || '']

    return langs.some((lang) => /^[a-z]{2,3}-CA\b/i.test(lang || ''))
}

function hasCanadianTimezone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
        return CANADIAN_TIMEZONES.has(tz)
    } catch (e) {
        // Very old browsers may not expose Intl.DateTimeFormat.resolvedOptions.
        // Treat as "unknown" — fail safe to USD default.
        return false
    }
}

/**
 * Auto-detection seed: BOTH locale AND timezone must point to Canada.
 * Conservative on purpose — we'd rather a Canadian see USD once and toggle
 * than a US user see CAD by mistake.
 */
export default function detectInitialCurrencyIsCAD() {
    if (typeof navigator === 'undefined') return false
    return hasCanadianLocale() && hasCanadianTimezone()
}

/**
 * Soft-prompt trigger: should we offer the user a CAD switch via banner?
 *
 * Returns true ONLY when there's a HIGH-SIGNAL MISMATCH that the strict
 * auto-detection above intentionally did not act on:
 *
 *   - Timezone IS Canadian (strong evidence the user is physically in Canada)
 *   - BUT locale is NOT *-CA (the visitor is on a default en-US browser,
 *     which describes most Canadian devices because en-US is the factory
 *     default and most users never change it)
 *
 * In this case auto-detection stayed on USD (correctly conservative), but
 * the visitor is probably Canadian and would prefer CAD. We surface a small
 * banner offering the switch — never auto-flipping, never showing US users
 * anything (their timezone disqualifies them).
 *
 * Returns false for anyone in a non-Canadian timezone, which means US users
 * NEVER see the banner regardless of locale, currency state, or anything else.
 */
export function shouldOfferCAD() {
    if (typeof navigator === 'undefined') return false
    return hasCanadianTimezone() && !hasCanadianLocale()
}
